/* eslint-disable react-hooks/exhaustive-deps */
import React, {createContext, useContext, useState} from 'react'
import {User} from './_models'

export interface PaginationLink {
  label: string
  active: boolean
  url: string | null
  page: number | null
}

export interface Pagination {
  totalItems: number
  totalPages: number
  currentPage: number
  itemsPerPage: number
  pageSize: number
  links: PaginationLink[]
}

export interface ResponseData {
  data: User[]
  payload?: {
    pagination: Pagination
  }
}

export interface QueryResponseContextProps {
  isLoading: boolean
  query: string
  refetch: () => void
  response: ResponseData | null
  dataResponse: ResponseData | null
  updateState: (data: ResponseData) => void
}

const QueryResponseContext = createContext<QueryResponseContextProps>({
  isLoading: false,
  query: '',
  refetch: () => {},
  response: null,
  dataResponse: null,
  updateState: () => {},
})

const QueryResponseProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [query, setQuery] = useState('')
  const [response, setResponse] = useState<ResponseData | null>(null)

  const refetch = () => {
    // Implement refetch logic here
  }

  const updateState = (data: ResponseData) => {
    setResponse(data)
  }

  return (
    <QueryResponseContext.Provider
      value={{
        isLoading,
        query,
        refetch,
        response,
        dataResponse: response,
        updateState,
      }}
    >
      {children}
    </QueryResponseContext.Provider>
  )
}

export const useQueryResponse = () => useContext(QueryResponseContext)

export const useQueryResponseData = () => {
  const {dataResponse} = useQueryResponse()
  return {
    data: dataResponse?.data || [],
    total: dataResponse?.payload?.pagination?.totalItems || 0,
  }
}

export const useQueryResponseLoading = () => {
  const {isLoading} = useQueryResponse()
  return isLoading
}

export const useQueryResponsePagination = () => {
  const {dataResponse} = useQueryResponse()
  if (!dataResponse?.payload?.pagination) return undefined
  const pag = dataResponse.payload.pagination
  const links: PaginationLink[] = []
  // Generate page links with 1-based numbering for display
  for (let page = 0; page < pag.totalPages; page++) {
    const isActive = page === pag.currentPage
    const url = isActive ? null : `?page=${page}`
    links.push({
      label: `${page + 1}`,
      active: isActive,
      url,
      page,
    })
  }
  // Add "prev" button if not on the first page
  if (pag.currentPage > 0) {
    links.unshift({
      label: 'Prev',
      active: false,
      url: `?page=${pag.currentPage - 1}`,
      page: pag.currentPage - 1,
    })
  }
  // Add "next" button if not on the last page
  if (pag.currentPage < pag.totalPages - 1) {
    links.push({
      label: 'Next',
      active: false,
      url: `?page=${pag.currentPage + 1}`,
      page: pag.currentPage + 1,
    })
  }
  return {
    ...pag,
    pageSize: 10 as 10,
    links,
  }
}

export {QueryResponseProvider}

